import {
  confirmResetPassword,
  confirmSignIn as cognitoConfirmSignIn,
  fetchAuthSession,
  fetchMFAPreference,
  getCurrentUser,
  rememberDevice,
  setUpTOTP as cognitoSetupTOTP,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  updateMFAPreference as cognitoUpdateMFAPreference,
  updatePassword as cognitoUpdatePassword,
  verifyTOTPSetup as cognitoVerifyTOTPSetup,
} from 'aws-amplify/auth'

import { ERROR_MESSAGES } from '@/constants/errorMessages'
import { withBaseApiUrl } from '@/utils/withBaseApiUrl'

import { ApiError, graphqlRequest, handleUnknownApiError } from '../api'
import { graphql } from '../gql'
import { AuthenticatedUser } from './types'

/** MiROHAのAPI Cognito認証で得たtokenを元にMiROHAのログインセッションを発行する */
const mirohaSignIn = async () => {
  try {
    const session = await fetchAuthSession()
    const idToken = session.tokens?.idToken?.toString()
    if (!idToken) {
      throw new ApiError(ERROR_MESSAGES.defaultApiError)
    }
    const res = await fetch(withBaseApiUrl('/accounts/signin'), {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${idToken}`,
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({
        isConnect: true,
      }),
    })
    if (!res.ok) {
      const errorMessage =
        res.status === 404
          ? ERROR_MESSAGES.invalidEmailOrPassword // cognitoで引っかかった場合と同じエラーメッセージを返す（stage1などではcognitoはpassしてMiROHAで失敗するケースがある）
          : res.statusText
      throw new ApiError(errorMessage, { status: res.status })
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    throw new ApiError(ERROR_MESSAGES.networkError, { cause: error })
  }
}

type SignInParam = {
  email: string
  password: string
}
type SignInResult = {
  result: 'done' | 'requiredTOTP' | 'requiredNewPassword'
}

export const signIn = async (param: SignInParam): Promise<SignInResult> => {
  try {
    // 別ユーザーでログインしている場合、一度ログアウトする
    await cognitoSignOut()
    const {
      nextStep: { signInStep },
    } = await cognitoSignIn({
      username: param.email,
      password: param.password,
    })
    if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { result: 'requiredNewPassword' }
    }
    if (signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      return { result: 'requiredTOTP' }
    }
    // Cognitoの認証が完了したのち、tokenを元にMiROHAの認証を行う
    if (signInStep === 'DONE') {
      await mirohaSignIn()
      return { result: 'done' }
    }
    throw new ApiError(ERROR_MESSAGES.defaultApiError)
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'NotAuthorizedException') {
        if (error.message === 'Password attempts exceeded') {
          throw new ApiError(ERROR_MESSAGES.accountLocked)
        }
        throw new ApiError(ERROR_MESSAGES.invalidEmailOrPassword)
      }
      if (error.name === 'UserNotFoundException') {
        throw new ApiError(ERROR_MESSAGES.invalidEmailOrPassword)
      }
    }
    throw handleUnknownApiError(error)
  }
}

type ConfirmSignInParam = {
  code: string
  rememberDevice?: boolean
}
/** 多要素認証が設定されている場合に、TOTP認証を完了する */
export const confirmSignIn = async (param: ConfirmSignInParam) => {
  // StorybookではCognitoのAPIを呼び出さない
  if (import.meta.env.STORYBOOK) {
    return
  }
  try {
    const {
      nextStep: { signInStep },
    } = await cognitoConfirmSignIn({
      challengeResponse: param.code,
    })
    if (param.rememberDevice) {
      await rememberDevice()
    }
    if (signInStep !== 'DONE') {
      throw new ApiError(ERROR_MESSAGES.defaultApiError)
    }
    await mirohaSignIn()
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'ExpiredCodeException') {
        throw new ApiError(ERROR_MESSAGES.codeExpired)
      }
      if (error.name === 'CodeMismatchException') {
        throw new ApiError(ERROR_MESSAGES.codeMismatch)
      }
      // cognitoのパスワード認証→TOTP認証間のセッションが切れた場合
      if (
        error.name === 'NotAuthorizedException' &&
        error.message === 'Invalid session for the user, session is expired.'
      ) {
        throw new ApiError(ERROR_MESSAGES.sessionExpired)
      }
    }
    throw handleUnknownApiError(error)
  }
}

export const fetchTOTPEnabled = async () => {
  if (import.meta.env.STORYBOOK) {
    return window.localStorage.getItem('mock_totp_enabled') === 'true'
  }
  try {
    const res = await fetchMFAPreference()
    return res.enabled?.includes('TOTP') ?? false
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'UserUnAuthenticatedException') {
        return false
      }
    }
    throw handleUnknownApiError(error)
  }
}

export const setupTOTPSecret = async () => {
  if (import.meta.env.STORYBOOK) {
    return crypto.getRandomValues(new Uint8Array(24)).join('')
  }
  try {
    const res = await cognitoSetupTOTP()
    return res.sharedSecret
  } catch (error) {
    throw handleUnknownApiError(error)
  }
}

type VerifyTOTPParam = {
  code: string
}
export const verifyTOTP = async (param: VerifyTOTPParam) => {
  if (import.meta.env.STORYBOOK) {
    window.localStorage.setItem('mock_totp_enabled', 'true')
    return
  }
  try {
    await cognitoVerifyTOTPSetup({
      code: param.code,
    })
    await cognitoUpdateMFAPreference({
      totp: 'PREFERRED',
    })
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'InvalidParameterException') {
        throw new ApiError(ERROR_MESSAGES.invalidCodeFormat)
      }
      if (error.name === 'EnableSoftwareTokenMFAException') {
        // もう少しスマートにハンドリングしたい
        if (
          error.message === 'Your software token has already been used once.'
        ) {
          throw new ApiError(ERROR_MESSAGES.codeAlreadyUsed)
        }
        throw new ApiError(ERROR_MESSAGES.codeMismatch)
      }
    }
    throw handleUnknownApiError(error)
  }
}

type DisableTOTPParam = {
  code: string
}
export const disableTOTP = async (param: DisableTOTPParam) => {
  if (import.meta.env.STORYBOOK) {
    window.localStorage.setItem('mock_totp_enabled', 'false')
    return
  }
  try {
    // 認証してOKだったら無効化
    await verifyTOTP({
      code: param.code,
    })
    await cognitoUpdateMFAPreference({
      totp: 'DISABLED',
    })
  } catch (error) {
    if (error instanceof ApiError) {
      throw error
    }
    throw handleUnknownApiError(error)
  }
}

type ForgotPasswordParam = {
  email: string
}
export const forgotPassword = async (param: ForgotPasswordParam) => {
  try {
    // MiROHAのAPIを呼び出す メールで仮パスワードが通知される
    const res = await fetch(
      withBaseApiUrl('/accounts/verify-forgot-password'),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: param.email }),
        credentials: 'include',
      },
    )
    if (!res.ok) {
      throw new ApiError(res.statusText, {
        status: res.status,
      })
    }
  } catch (error) {
    if (error instanceof ApiError) {
      if (error.status === 404) {
        throw new ApiError(ERROR_MESSAGES.emailNotExists, {
          cause: error,
          status: 404,
        })
      }
      throw handleUnknownApiError(error)
    }
    throw new ApiError(ERROR_MESSAGES.networkError, { cause: error })
  }
}
type ConfirmForgotPasswordParam = {
  email: string
  code: string
  newPassword: string
}
export const confirmForgotPassword = async (
  param: ConfirmForgotPasswordParam,
) => {
  if (import.meta.env.STORYBOOK === 'true') {
    // Storybookでは、CognitoのAPIを呼び出さない
    return
  }
  try {
    await confirmResetPassword({
      username: param.email,
      confirmationCode: param.code,
      newPassword: param.newPassword,
    })
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'ConfirmForgotPasswordException') {
        throw new ApiError(ERROR_MESSAGES.invalidCodeOrPassword)
      }
      if (error.name === 'CodeMismatchException') {
        throw new ApiError(ERROR_MESSAGES.codeMismatchForgotPassword)
      }
      if (error.name === 'ExpiredCodeException') {
        throw new ApiError(ERROR_MESSAGES.codeExpiredForgotPassword)
      }
    }
    throw handleUnknownApiError(error)
  }
}

type ConfirmNewPasswordParam = {
  newPassword: string
}

type UpdatePasswordParam = {
  oldPassword: string
  newPassword: string
}

export const updatePassword = async (param: UpdatePasswordParam) => {
  try {
    await cognitoUpdatePassword({
      oldPassword: param.oldPassword,
      newPassword: param.newPassword,
    })
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'NotAuthorizedException') {
        throw new ApiError(ERROR_MESSAGES.invalidCurrentPassword)
      }
    }
    throw handleUnknownApiError(error)
  }
}

export const confirmNewPassword = async (param: ConfirmNewPasswordParam) => {
  try {
    const {
      nextStep: { signInStep },
    } = await cognitoConfirmSignIn({
      challengeResponse: param.newPassword,
    })
    if (signInStep !== 'DONE') {
      throw new ApiError(ERROR_MESSAGES.defaultApiError)
    }
    await mirohaSignIn()
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'SignInException') {
        throw new ApiError(ERROR_MESSAGES.sessionExpired)
      }
    }
    throw handleUnknownApiError(error)
  }
}

const mirohaSignOut = async () => {
  try {
    const res = await fetch(withBaseApiUrl('/accounts/signout'), {
      method: 'DELETE',
      credentials: 'include',
    })
    if (!res.ok) {
      throw new ApiError(res.statusText, {
        status: res.status,
      })
    }
  } catch (error) {
    throw new ApiError(ERROR_MESSAGES.networkError, { cause: error })
  }
}

export const signOut = async () => {
  try {
    await cognitoSignOut()
    await mirohaSignOut()
  } catch (error) {
    if (error instanceof ApiError) {
      // 未認証の場合はエラーとして扱わない
      if (error.status === 401) {
        return
      }
    }
    throw handleUnknownApiError(error)
  }
}

const getAuthenticatedUserQuery = graphql(`
  query GetAuthenticatedUser {
    user {
      ... on BusinessUser {
        userUid
        organizationName
        generalUserUid
        generalUser {
          uid
          email
          firstName
          lastName
        }
        allNetworksRole {
          userRoleUid
          permissions
        }
        networks {
          networkUid
          userRoleUid
          userUid
          network {
            networkUid
            networkNumber
            networkDetail {
              networkName
            }
          }
          role {
            userRoleUid
            roleName
            permissions
          }
        }
      }
    }
  }
`)

const getMirohaUser = async (): Promise<AuthenticatedUser> => {
  const res = await graphqlRequest(getAuthenticatedUserQuery, {})
  return res.user
}

export const getAuthenticatedUser = async (): Promise<AuthenticatedUser> => {
  try {
    // cognitoのセッションが取得できずエラーになれば、未認証として扱う
    await getCurrentUser()
    const mirohaUser = await getMirohaUser()
    return mirohaUser
  } catch (error) {
    if (
      error instanceof Error &&
      error.name === 'UserUnAuthenticatedException'
    ) {
      // 別タブでログアウトした場合など、Cognitoのユーザーが取得できない場合はrootで401エラーとして扱えるようにstatusをセットする
      throw new ApiError(error.message, { status: 401, cause: error })
    }
    throw handleUnknownApiError(error)
  }
}

type Agreements = {
  agreeTermsOfService: boolean
  agreePrivacyPolicy: boolean
  agreeExternalTransmissionPolicy: boolean
}
export const getAgreements = async (): Promise<Agreements> => {
  try {
    const res = await fetch(withBaseApiUrl('/accounts/agreement'), {
      credentials: 'include',
    })
    if (!res.ok) {
      throw new ApiError(res.statusText, { status: res.status })
    }
    const data = await res.json()
    return {
      agreePrivacyPolicy: data.agree_privacy_policy,
      agreeTermsOfService: data.agree_terms_of_service,
      agreeExternalTransmissionPolicy: data.agree_external_transmission_policy,
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw handleUnknownApiError(error)
    }
    throw new ApiError(ERROR_MESSAGES.networkError, { cause: error })
  }
}

export const createAgreements = async (agreements: Agreements) => {
  try {
    const res = await fetch(withBaseApiUrl('/accounts/agreement'), {
      method: 'POST',
      body: JSON.stringify({
        agree_terms_of_service: agreements.agreeTermsOfService,
        agree_privacy_policy: agreements.agreePrivacyPolicy,
        agree_external_transmission_policy:
          agreements.agreeExternalTransmissionPolicy,
      }),
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
    if (!res.ok) {
      throw new ApiError(res.statusText, { status: res.status })
    }
  } catch (error) {
    if (error instanceof ApiError) {
      throw handleUnknownApiError(error)
    }
    throw new ApiError(ERROR_MESSAGES.networkError, { cause: error })
  }
}

type AuthState =
  | {
      isAuthenticated: true
      // 規約への同意が必要
      shouldAgreeTerms: boolean
      user: AuthenticatedUser
    }
  | {
      isAuthenticated: false
      shouldAgreeTerms?: undefined
      user?: undefined
    }
export const getAuthState = async (): Promise<AuthState> => {
  try {
    const user = await getAuthenticatedUser()
    const agreements = await getAgreements()
    return {
      isAuthenticated: true,
      shouldAgreeTerms: Object.values(agreements).some(v => !v),
      user,
    }
  } catch {
    return {
      isAuthenticated: false,
    }
  }
}
